import { supabase } from '../lib/supabase';

// ============================================================
// PARTNER OUTCOME SNAPSHOTS SERVICE
// ============================================================
export const partnerOutcomeSnapshotsService = {
  async getByPartner(partnerId) {
    const { data, error } = await supabase
      ?.from('partner_outcome_snapshots')
      ?.select('*')
      ?.eq('partner_id', partnerId)
      ?.order('snapshot_date', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async getAll(filters = {}) {
    let query = supabase
      ?.from('partner_outcome_snapshots')
      ?.select('*, partners(legal_name, brand_name, tier)')
      ?.order('snapshot_date', { ascending: false });
    if (filters?.partnerId) query = query?.eq('partner_id', filters?.partnerId);
    if (filters?.quarter) query = query?.eq('quarter', filters?.quarter);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getLatestByPartner(partnerId) {
    const { data, error } = await supabase
      ?.from('partner_outcome_snapshots')
      ?.select('*')
      ?.eq('partner_id', partnerId)
      ?.order('snapshot_date', { ascending: false })
      ?.limit(1)
      ?.maybeSingle();
    if (error) throw error;
    return data;
  },

  async getBaselineByPartner(partnerId) {
    const { data, error } = await supabase
      ?.from('partner_outcome_snapshots')
      ?.select('*')
      ?.eq('partner_id', partnerId)
      ?.eq('snapshot_type', 'baseline')
      ?.order('snapshot_date', { ascending: true })
      ?.limit(1)
      ?.maybeSingle();
    if (error) throw error;
    return data;
  },

  async create(snapshotData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('partner_outcome_snapshots')
      ?.insert({ ...snapshotData, created_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('partner_outcome_snapshots')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('partner_outcome_snapshots')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// OUTCOME TARGETS SERVICE
// ============================================================
export const outcomeTargetsService = {
  async getAll(year) {
    let query = supabase
      ?.from('outcome_targets')
      ?.select('*')
      ?.order('tier', { ascending: true });
    if (year) query = query?.eq('target_year', year);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getByTier(tier, year) {
    const { data, error } = await supabase
      ?.from('outcome_targets')
      ?.select('*')
      ?.eq('tier', tier)
      ?.eq('target_year', year || new Date()?.getFullYear())
      ?.limit(1)
      ?.maybeSingle();
    if (error) throw error;
    return data;
  },

  async upsert(targetData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('outcome_targets')
      ?.upsert({ ...targetData, created_by: user?.id, updated_at: new Date()?.toISOString() }, { onConflict: 'id' })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// QUARTERLY REVIEWS SERVICE
// ============================================================
export const quarterlyReviewsService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('quarterly_reviews')
      ?.select('*, partners(legal_name, brand_name, tier)')
      ?.order('review_date', { ascending: false });
    if (filters?.partnerId) query = query?.eq('partner_id', filters?.partnerId);
    if (filters?.quarter) query = query?.eq('quarter', filters?.quarter);
    if (filters?.status) query = query?.eq('status', filters?.status);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getByPartner(partnerId) {
    const { data, error } = await supabase
      ?.from('quarterly_reviews')
      ?.select('*')
      ?.eq('partner_id', partnerId)
      ?.order('review_date', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async create(reviewData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('quarterly_reviews')
      ?.insert({ ...reviewData, reviewer_id: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('quarterly_reviews')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('quarterly_reviews')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// ECITY MEMBER OUTCOMES SERVICE
// ============================================================
export const ecityMemberOutcomesService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('ecity_member_outcomes')
      ?.select('*, training_programs(program_name, delivering_partner_id, partners(brand_name, legal_name))')
      ?.order('created_at', { ascending: false });
    if (filters?.programId) query = query?.eq('program_id', filters?.programId);
    if (filters?.attendanceStatus && filters?.attendanceStatus !== 'all') {
      query = query?.eq('attendance_status', filters?.attendanceStatus);
    }
    if (filters?.memberEmail) query = query?.eq('member_email', filters?.memberEmail);
    if (filters?.search) query = query?.ilike('member_name', `%${filters?.search}%`);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getByProgram(programId) {
    const { data, error } = await supabase
      ?.from('ecity_member_outcomes')
      ?.select('*')
      ?.eq('program_id', programId)
      ?.order('member_name', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async create(outcomeData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('ecity_member_outcomes')
      ?.insert({ ...outcomeData, recorded_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('ecity_member_outcomes')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('ecity_member_outcomes')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// DEAL CORRELATION SERVICE
// ============================================================
export const enablementDealCorrelationService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('enablement_deal_correlation')
      ?.select('*, partners(legal_name, brand_name, tier)')
      ?.order('period_end', { ascending: false });
    if (filters?.partnerId) query = query?.eq('partner_id', filters?.partnerId);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getByPartner(partnerId) {
    const { data, error } = await supabase
      ?.from('enablement_deal_correlation')
      ?.select('*')
      ?.eq('partner_id', partnerId)
      ?.order('period_end', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async create(correlationData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('enablement_deal_correlation')
      ?.insert({ ...correlationData, created_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// OVERVIEW STATS SERVICE (aggregated for dashboard)
// ============================================================
export const enablementOverviewService = {
  async getStats() {
    try {
      const [
        activePrograms,
        pendingRequests,
        expiringCerts,
        latestAssessmentsResult
      ] = await Promise.all([
        supabase?.from('training_programs')?.select('*', { count: 'exact', head: true })?.eq('program_status', 'approved'),
        supabase?.from('training_requests')?.select('*', { count: 'exact', head: true })?.eq('request_status', 'requested'),
        supabase?.from('partner_certifications')?.select('*', { count: 'exact', head: true })
          ?.lte('expiry_date', new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0])
          ?.gte('expiry_date', new Date()?.toISOString()?.split('T')?.[0]),
        supabase?.from('capability_assessments')?.select('overall_score')?.order('assessment_date', { ascending: false })?.limit(20)
      ]);

      const latestAssessments = latestAssessmentsResult?.data;

      const avgScore = latestAssessments?.length
        ? Math.round(latestAssessments?.reduce((s, a) => s + (a?.overall_score || 0), 0) / latestAssessments?.length)
        : 0;

      return {
        activePrograms: activePrograms?.count || 0,
        pendingRequests: pendingRequests?.count || 0,
        expiringCerts: expiringCerts?.count || 0,
        avgCapabilityScore: avgScore
      };
    } catch (err) {
      console.error('Overview stats error:', err);
      return { activePrograms: 0, pendingRequests: 0, expiringCerts: 0, avgCapabilityScore: 0 };
    }
  },

  async getPartnerEnablementStatus() {
    const { data, error } = await supabase
      ?.from('partners')
      ?.select(`
        id, brand_name, legal_name, tier,
        capability_assessments(overall_score, assessment_date),
        partner_certifications(id, cert_status),
        partner_training_assignments(id, assignment_status)
      `)
      ?.order('brand_name', { ascending: true })
      ?.limit(10);
    if (error) throw error;
    return (data || [])?.map(p => {
      const latestAssessment = p?.capability_assessments?.sort((a, b) =>
        new Date(b?.assessment_date) - new Date(a?.assessment_date)
      )?.[0];
      const activeCerts = p?.partner_certifications?.filter(c => c?.cert_status === 'active')?.length || 0;
      const completedTrainings = p?.partner_training_assignments?.filter(t => t?.assignment_status === 'completed')?.length || 0;
      const totalTrainings = p?.partner_training_assignments?.length || 0;
      const completionPct = totalTrainings > 0 ? Math.round((completedTrainings / totalTrainings) * 100) : 0;
      return {
        id: p?.id,
        name: p?.brand_name || p?.legal_name,
        tier: p?.tier,
        capabilityScore: latestAssessment?.overall_score || 0,
        lastAssessed: latestAssessment?.assessment_date,
        activeCerts,
        completionPct
      };
    });
  }
};
